import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import { HERO_IMAGE, STUDIO } from "@/data/site";
import { scrollTo } from "./Navigation";

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative h-screen min-h-[680px] w-full overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Wedding moment by lsb-studio"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="overline text-[#E6D3A3] mb-6"
        >
          A photography studio · est. since the love of light
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.35 }}
          className="font-serif-display text-white text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-tight max-w-5xl"
        >
          Capturing Life&apos;s
          <br />
          <span className="italic font-light">most elegant</span> moments.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.55 }}
          className="text-white/85 text-base md:text-lg mt-8 max-w-xl leading-relaxed"
        >
          Heirloom imagery for weddings, maternity, events and portraits —
          crafted with cinematic light and quiet patience.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.75 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <Link
            data-testid="hero-book-btn"
            to="/book"
            className="px-9 py-4 bg-[#FDFBF7] text-[#1A1A1A] text-xs uppercase tracking-[0.28em] font-medium hover:bg-[#C5A059] hover:text-white transition-colors duration-500 cursor-pointer inline-block"
          >
            Book Your Session
          </Link>
          <a
            data-testid="hero-portfolio-btn"
            href="#gallery"
            onClick={(e) => { e.preventDefault(); scrollTo("gallery"); }}
            className="link-underline text-white text-xs uppercase tracking-[0.28em] font-medium cursor-pointer"
          >
            View Portfolio
          </a>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
}
